import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Autocomplete, Box, Checkbox, ListItem, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CollapsibleSection from '../Button/CollapsibleSection'
import { Project, RequestProps, User } from '../../store/types'
import FilterMenu from '../FilterMenu/FilterMenu'
import { isEqual } from 'lodash'
import { ProjectAcronym, useGetAcronymsQuery, useGetProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import defaultProps from '../../config/defaultProps'
import { useGetLightLeadersQuery, useGetLightUsersQuery } from '../../store/redux/reducers/usersSlice'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { getSelectedDate } from '../../store/redux/reducers/monthPickerSlice'
import { RootState } from '../../store/redux/store'
import { VacationRequestProps } from '../../store/types/utils/VacationTypes'
import { useLeftPadding } from '../../store/redux/hooks'
import {
  getIsEmployee,
  getIsLeader,
  getIsLeaderOrManagement,
  getIsManagement,
  getIsProjectLeader,
} from '../../store/redux/reducers/currentUserSlice'

export type FilterType = {
  employees: string[]
  leaders: string[]
  projects: string[]
  myEmployees: string[]
}

export type SearchEmployee = {
  name: string
  id: string
}

export const initialFilters: FilterType = {
  employees: [],
  leaders: [],
  projects: [],
  myEmployees: [],
}

interface FilterVacationsMenuProps {
  requestProps: VacationRequestProps
  setRequestProps: React.Dispatch<React.SetStateAction<VacationRequestProps>>
  currentUser: User
  isOpen: boolean
  searchEmployees: SearchEmployee[]
}

const FilterVacationsMenu = forwardRef<HTMLDivElement, FilterVacationsMenuProps>(
  ({ requestProps, setRequestProps, currentUser, isOpen, searchEmployees }, ref) => {
    const { t } = useTranslation()
    const leftPadding = useLeftPadding()
    const [projectRequestProps, setProjectRequestProps] = useState<RequestProps>({
      ...defaultProps,
      sortByWorkHours: false,
    })
    const [leaderRequestProps] = useState<RequestProps>(defaultProps)
    const [leaderUsersRequestProps, setLeaderUsersRequestProps] = useState<RequestProps>(defaultProps)
    const [leaderProjectRequestProps, setLeaderProjectRequestProps] = useState<RequestProps>(defaultProps)
    const [selectedFilters, setSelectedFilters] = useState<FilterType>(initialFilters)
    const [projects, setProjects] = useState<ProjectAcronym[]>([])
    const [leaderProjectName, setLeaderProjectName] = useState<Project[]>([])
    const [leaderProject, setLeaderProject] = useState<Project[]>([])
    const [projectName, setProjectName] = useState<ProjectAcronym[]>([])
    const [searchedEmployees, setSearchedEmployees] = useState<SearchEmployee[]>([])
    const [leaders, setLeaders] = useState<User[]>([])
    const [leaderName, setLeaderName] = useState<User[]>([])
    const [leadersUsers, setLeadersUsers] = useState<User[]>([])
    const [leaderUsersName, setLeaderUsersName] = useState<User[]>([])
    const [leaderProjectPage, setLeaderProjectPage] = useState<number>(0)
    const [projectPage, setProjectPage] = useState<number>(0)
    const [leadersUsersPage, setLeadersUsersPage] = useState<number>(0)
    const selectedDateFromState = useSelector((state: RootState) => getSelectedDate(state))
    const selectedDate = useMemo(() => dayjs(selectedDateFromState), [selectedDateFromState])
    const [inputValue, setInputValue] = useState<string>('')
    const innerRef = useRef<HTMLDivElement>(null)
    const { isLeaderOrManagement, isEmployee, isLeader, isProjectLeader, isManagement } = useSelector(
      (state: RootState) => ({
        isLeaderOrManagement: getIsLeaderOrManagement(state),
        isEmployee: getIsEmployee(state),
        isLeader: getIsLeader(state),
        isProjectLeader: getIsProjectLeader(state),
        isManagement: getIsManagement(state),
      })
    )
    const isOnlyEmployee = isEmployee && !isLeaderOrManagement

    useImperativeHandle(ref, () => innerRef.current || document.createElement('div'))

    const { data: projectsResult, isSuccess: isProjectsSuccess } = useGetAcronymsQuery(projectRequestProps, {
      skip: !isOpen || isOnlyEmployee,
    })
    const { data: projectsResultLeader, isSuccess: isProjectsLeaderSuccess } = useGetProjectsQuery(
      leaderProjectRequestProps,
      { skip: !isOpen || isOnlyEmployee }
    )
    const { data: leaderData, isSuccess: isLeaderSuccess } = useGetLightLeadersQuery(leaderRequestProps, {
      skip: !isOpen || isOnlyEmployee,
    })
    const { data: leaderUsersData, isSuccess: isLeaderUsersSuccess } = useGetLightUsersQuery(leaderUsersRequestProps, {
      skip: !isOpen || isOnlyEmployee,
    })

    useEffect(() => {
      if (isLeader) {
        setLeaderProjectRequestProps({
          ...defaultProps,
          projectStatus: 'ACTIVE',
          'leaders.leader.id': currentUser?.id,
        })
        setLeaderUsersRequestProps({
          ...defaultProps,
          employeeStatus: 'ACTIVE',
          'leaders.id': currentUser?.id,
        })
      }
    }, [currentUser, isLeader])

    useEffect(() => {
      if (isLeaderSuccess) {
        setLeaders(Array.isArray(leaderData.content) ? leaderData.content : [])
      }
    }, [leaderData, isLeaderSuccess])

    useEffect(() => {
      if (isProjectsSuccess && projectsResult.content) {
        setProjects(prevUsers => {
          const newUsers = Array.isArray(projectsResult.content) ? projectsResult.content : []
          const existingUserIds = new Set(prevUsers.map(user => user.id))
          const filteredNewUsers = newUsers.filter(user => !existingUserIds.has(user.id))
          return [...prevUsers, ...filteredNewUsers]
        })
        setProjectPage(projectsResult.number)
      }
    }, [projectsResult, isProjectsSuccess])

    useEffect(() => {
      if (isLeaderUsersSuccess && leaderUsersData.content) {
        setLeadersUsers(prevUsers => {
          const newUsers = Array.isArray(leaderUsersData.content) ? leaderUsersData.content : []
          const existingUserIds = new Set(prevUsers.map(user => user.id))
          const filteredNewUsers = newUsers.filter(user => !existingUserIds.has(user.id))
          return [...prevUsers, ...filteredNewUsers]
        })
        setLeadersUsersPage(leaderUsersData.number)
      }
    }, [leaderUsersData, isLeaderUsersSuccess])

    useEffect(() => {
      if (isProjectsLeaderSuccess && projectsResultLeader.content) {
        setLeaderProject(prevUsers => {
          const newUsers = Array.isArray(projectsResultLeader.content) ? projectsResultLeader.content : []
          const existingUserIds = new Set(prevUsers.map(user => user.id))
          const filteredNewUsers = newUsers.filter(user => !existingUserIds.has(user.id))
          return [...prevUsers, ...filteredNewUsers]
        })
        setLeaderProjectPage(projectsResultLeader.number)
      }
    }, [projectsResultLeader, isProjectsLeaderSuccess])

    const handleFiltering = () => {
      let newProps: VacationRequestProps = { ...requestProps, page: 0 }

      if (selectedFilters.employees.length !== 0) {
        newProps = { ...newProps, employees: selectedFilters.employees }
      } else {
        newProps = { ...newProps, employees: undefined }
      }

      if (selectedFilters.projects.length !== 0) {
        newProps = { ...newProps, projects: selectedFilters.projects }
      } else {
        newProps = { ...newProps, projects: undefined }
      }

      if (selectedFilters.leaders.length !== 0) {
        newProps = { ...newProps, leaders: selectedFilters.leaders }
      } else {
        newProps = { ...newProps, leaders: undefined }
      }

      if (selectedFilters.myEmployees.length !== 0) {
        newProps = { ...newProps, myemployees: selectedFilters.myEmployees }
      } else {
        newProps = { ...newProps, myemployees: undefined }
      }

      if (isEqual(requestProps, newProps)) return

      setRequestProps({ ...newProps })
    }
    const handleClear = () => {
      setSelectedFilters(initialFilters)
      setProjectName([])
      setSearchedEmployees([])
      setLeaderName([])
      setLeaderUsersName([])
      setLeaderProjectName([])
      setInputValue('')
      setRequestProps({
        page: 0,
        size: requestProps.size,
        start: dayjs(selectedDate).startOf('month').format('YYYY-MM-DD'),
        end: dayjs(selectedDate).endOf('month').format('YYYY-MM-DD'),
      })
    }

    const icon = <CheckBoxOutlineBlank fontSize="small" />
    const checkedIcon = <CheckBox fontSize="small" />

    const handleLeaderProjectPageChange = () => {
      setLeaderProjectRequestProps({
        ...leaderProjectRequestProps,
        page: leaderProjectPage + 1,
      })
    }

    const handleProjectPageChange = () => {
      setProjectRequestProps({
        ...projectRequestProps,
        page: projectPage + 1,
      })
    }

    const handleLeaderUserPageChange = () => {
      setLeaderUsersRequestProps({
        ...leaderUsersRequestProps,
        page: leadersUsersPage + 1,
      })
    }
    const observer = useRef<IntersectionObserver | null>(null)

    const handleProjectPageChangeCallback = useCallback(handleProjectPageChange, [handleProjectPageChange])
    const handleLeaderUsersPageChangeCallback = useCallback(handleLeaderUserPageChange, [handleLeaderUserPageChange])
    const handleLeaderProjectPageChangeCallback = useCallback(handleLeaderProjectPageChange, [
      handleLeaderProjectPageChange,
    ])

    const lastLeaderElementRef = useCallback(
      (node: HTMLElement | null) => {
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && leaderUsersData && !leaderUsersData.last) {
            handleLeaderUsersPageChangeCallback()
          }
        })
        if (node) observer.current.observe(node)
      },
      [handleLeaderUsersPageChangeCallback, leaderUsersData]
    )

    const lastLeaderProjectElementRef = useCallback(
      (node: HTMLElement | null) => {
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && projectsResultLeader && !projectsResultLeader.last) {
            handleLeaderProjectPageChangeCallback()
          }
        })
        if (node) observer.current.observe(node)
      },
      [handleLeaderProjectPageChangeCallback, projectsResultLeader]
    )

    const lastProjectElementRef = useCallback(
      (node: HTMLElement | null) => {
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && projectsResult && !projectsResult.last) {
            handleProjectPageChangeCallback()
          }
        })
        if (node) observer.current.observe(node)
      },
      [handleProjectPageChangeCallback, projectsResult]
    )

    const handleInputChange = (_: React.ChangeEvent<{}>, value: string) => {
      setInputValue(value)
    }

    return (
      <Box ref={innerRef} sx={{ height: '100%' }}>
        <FilterMenu filter={handleFiltering} clear={handleClear}>
          <CollapsibleSection text={t('page.users.filtering.employees')}>
            <Autocomplete
              limitTags={3}
              sx={{ maxWidth: 'md', pl: leftPadding }}
              multiple
              id="employees-autocomplete-filter"
              size="small"
              disableCloseOnSelect
              options={searchEmployees}
              value={searchedEmployees}
              onChange={(_, value) => {
                setSelectedFilters(prev => ({
                  ...prev,
                  employees: value.map(p => p.id),
                }))
                setSearchedEmployees(value)
              }}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              noOptionsText={t('noOptionsFound')}
              getOptionLabel={option => option.name}
              renderOption={(props, option, { selected }) => {
                const { key, ...restProps } = props
                return (
                  <React.Fragment key={option.id}>
                    <ListItem {...restProps}>
                      <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                      {`${option.name}`}
                    </ListItem>
                  </React.Fragment>
                )
              }}
              renderInput={params => (
                <TextField
                  id="employees-autocomplete-filter"
                  size="small"
                  fullWidth
                  inputProps={params.inputProps}
                  InputProps={params.InputProps}
                  label={t('page.users.filtering.employees')}
                  placeholder={t('page.users.filtering.employees')}
                />
              )}
            />
          </CollapsibleSection>
          {currentUser && isProjectLeader && !isManagement ? (
            <CollapsibleSection text={t('page.users.filtering.projects')}>
              <Autocomplete
                limitTags={3}
                sx={{ maxWidth: 'md', pl: leftPadding }}
                multiple
                id="leader-projects-autocomplete-filter"
                size="small"
                disableCloseOnSelect
                options={leaderProject as Project[]}
                value={leaderProjectName}
                onChange={(_, value) => {
                  setSelectedFilters(prev => ({
                    ...prev,
                    projects: value.map(p => p.id),
                  }))

                  setLeaderProjectName(value)
                }}
                noOptionsText={t('noOptionsFound')}
                getOptionLabel={option => option.acronym}
                renderOption={(props, option, { selected }) => {
                  const listItemRef =
                    option === leaderProject[leaderProject.length - 1] ? lastLeaderProjectElementRef : null
                  const { key, ...restProps } = props
                  return (
                    <React.Fragment key={option.id}>
                      <ListItem ref={listItemRef} {...restProps}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option.acronym}
                      </ListItem>
                      {option === leaderProject[leaderProject.length - 1] && (
                        <Box alignItems="center" justifyContent="center">
                          <ListItem key={'last-item-2'}>
                            {projectsResultLeader && !projectsResultLeader.last ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.loadMore')}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.allLoadedProjects')}
                              </Typography>
                            )}
                          </ListItem>
                        </Box>
                      )}
                    </React.Fragment>
                  )
                }}
                renderInput={params => (
                  <TextField
                    id="leader-projects-autocomplete-filter"
                    size="small"
                    fullWidth
                    inputProps={params.inputProps}
                    InputProps={params.InputProps}
                    label={t('page.users.filtering.projects')}
                    placeholder={t('page.users.filtering.projects')}
                  />
                )}
              />
            </CollapsibleSection>
          ) : currentUser && isManagement ? (
            <CollapsibleSection text={t('page.users.filtering.projects')}>
              <Autocomplete
                limitTags={3}
                sx={{ maxWidth: 'md', pl: leftPadding }}
                multiple
                id="projects-autocomplete-filter"
                size="small"
                disableCloseOnSelect
                options={projects as ProjectAcronym[]}
                value={projectName}
                onChange={(_, value) => {
                  setSelectedFilters(prev => ({
                    ...prev,
                    projects: value.map(p => p.id),
                  }))

                  setProjectName(value)
                }}
                noOptionsText={t('noOptionsFound')}
                getOptionLabel={option => option.acronym}
                renderOption={(props, option, { selected }) => {
                  const listItemRef = option === projects[projects.length - 1] ? lastProjectElementRef : null
                  const { key, ...restProps } = props
                  return (
                    <React.Fragment key={option.id}>
                      <ListItem ref={listItemRef} {...restProps}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option.acronym}
                      </ListItem>
                      {option === projects[projects.length - 1] && (
                        <Box alignItems="center" justifyContent="center">
                          <ListItem key={'last-item-3'}>
                            {projectsResult && !projectsResult.last ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.loadMore')}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.allLoadedProjects')}
                              </Typography>
                            )}
                          </ListItem>
                        </Box>
                      )}
                    </React.Fragment>
                  )
                }}
                renderInput={params => (
                  <TextField
                    id="projects-autocomplete-filter"
                    size="small"
                    fullWidth
                    inputProps={params.inputProps}
                    InputProps={params.InputProps}
                    label={t('page.users.filtering.projects')}
                    placeholder={t('page.users.filtering.projects')}
                  />
                )}
              />
            </CollapsibleSection>
          ) : null}
          {isManagement && (
            <CollapsibleSection text={t('page.users.filtering.leaders')}>
              <Autocomplete
                sx={{ pl: leftPadding }}
                limitTags={3}
                multiple
                id="leader-autocomplete-filter"
                size="small"
                disableCloseOnSelect
                options={leaders as User[]}
                value={leaderName}
                onChange={(_, value) => {
                  setSelectedFilters(prev => ({
                    ...prev,
                    leaders: value.map(p => p.id),
                  }))

                  setLeaderName(value)
                }}
                noOptionsText={t('noOptionsFound')}
                getOptionLabel={option => option.firstName}
                renderOption={(props, option, { selected }) => {
                  const { key, ...restProps } = props
                  return (
                    <React.Fragment key={option.id}>
                      <ListItem {...restProps}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {`${option.firstName} ${option.lastName}`}
                      </ListItem>
                    </React.Fragment>
                  )
                }}
                renderInput={params => (
                  <TextField
                    id="leader-autocomplete-filter"
                    size="small"
                    fullWidth
                    inputProps={params.inputProps}
                    InputProps={params.InputProps}
                    label={t('page.users.filtering.leaders')}
                    placeholder={t('page.users.filtering.leaders')}
                  />
                )}
              />
            </CollapsibleSection>
          )}
          {isLeader && (
            <CollapsibleSection text={t('page.users.filtering.myEmployees')}>
              <Autocomplete
                limitTags={3}
                sx={{ maxWidth: 'md', pl: leftPadding }}
                multiple
                id="parent-autocomplete-employees"
                size="small"
                disableCloseOnSelect
                options={leadersUsers as User[]}
                value={leaderUsersName}
                onChange={(_, value) => {
                  setSelectedFilters(prev => ({
                    ...prev,
                    myEmployees: value.map(p => p.id),
                  }))

                  setLeaderUsersName(value)
                }}
                noOptionsText={t('noOptionsFound')}
                getOptionLabel={option => option.firstName}
                renderOption={(props, option, { selected }) => {
                  const listItemRef = option === leadersUsers[leadersUsers.length - 1] ? lastLeaderElementRef : null
                  const { key, ...restProps } = props
                  return (
                    <React.Fragment key={option.id}>
                      <ListItem ref={listItemRef} {...restProps}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {`${option.firstName} ${option.lastName}`}
                      </ListItem>
                      {option === leadersUsers[leadersUsers.length - 1] && (
                        <Box alignItems="center" justifyContent="center">
                          <ListItem key={'last-item-4'}>
                            {leaderUsersData && !leaderUsersData.last ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.loadMore')}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ fontSize: '10px' }}
                              >
                                {t('page.users.filtering.allLoaded')}
                              </Typography>
                            )}
                          </ListItem>
                        </Box>
                      )}
                    </React.Fragment>
                  )
                }}
                renderInput={params => (
                  <TextField
                    aria-label={t('page.users.filtering.employees')}
                    id="parent-autocomplete-employees"
                    size="small"
                    fullWidth
                    inputProps={params.inputProps}
                    InputProps={params.InputProps}
                    label={t('page.users.filtering.employees')}
                    placeholder={t('page.users.filtering.employees')}
                  />
                )}
              />
            </CollapsibleSection>
          )}
        </FilterMenu>
      </Box>
    )
  }
)

export default FilterVacationsMenu
